import { memo, useState, useRef, useEffect } from 'preact/compat';
import { useAppDispatch, useAppSelector } from '../hooks';
import { setFilter, resetFilters } from '../store/offersSlice';
import { selectUniqueVendors } from '../store/selectors';

const FilterBar = memo(() => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector(s => s.offers.filters);
    const uniqueVendors = useAppSelector(selectUniqueVendors);
    const offerStatuses = useAppSelector(s => s.references.data?.offer_statuses ?? []);
    const buyers = useAppSelector(s => s.references.data?.buyers ?? []);

    const [open, setOpen] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);

    const sortedVendors = [...uniqueVendors].sort((a, b) => a.localeCompare(b, 'ru'));
    const sortedStatuses = [...offerStatuses].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
    const sortedBuyers = [...buyers].sort((a, b) => (a.buyer_name || '').localeCompare(b.buyer_name || '', 'ru'));

    const hasActiveFilters = filters.status_code !== 'all' || filters.buyer_code !== 'all' || filters.vendor_name !== 'all' || filters.search !== '' || filters.protection || filters.my;

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={popupRef}>
            <button
                className={`btn btn-sm btn-ghost p-1 ${hasActiveFilters ? 'text-accent' : 'text-base-content/50'}`}
                onClick={() => setOpen(!open)}
                title="Фильтры"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {hasActiveFilters && <span className="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full" />}
            </button>

            {open && (
                <div className="absolute top-full left-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl z-50 w-64 p-3 flex flex-col gap-2">
                    <input type="text" className="input input-bordered input-sm w-full" placeholder="Поиск..."
                        value={filters.search} onInput={e => dispatch(setFilter({ search: (e.target as HTMLInputElement).value }))} />

                    <select className="select select-bordered select-sm w-full" value={filters.status_code}
                        onChange={e => dispatch(setFilter({ status_code: (e.target as HTMLSelectElement).value }))}>
                        <option value="all">Статус</option>
                        {sortedStatuses.map(s => <option key={s.code} value={String(s.code)}>{s.name}</option>)}
                    </select>

                    <select className="select select-bordered select-sm w-full" value={filters.vendor_name}
                        onChange={e => dispatch(setFilter({ vendor_name: (e.target as HTMLSelectElement).value }))}>
                        <option value="all">Поставщик</option>
                        {sortedVendors.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>

                    <select className="select select-bordered select-sm w-full" value={filters.buyer_code}
                        onChange={e => dispatch(setFilter({ buyer_code: (e.target as HTMLSelectElement).value }))}>
                        <option value="all">Байер</option>
                        {sortedBuyers.map(b => <option key={b.code} value={String(b.code)}>{b.buyer_name}</option>)}
                    </select>

                    <div className="flex gap-2">
                        <label className="cursor-pointer flex-1">
                            <input type="checkbox" className="hidden" checked={filters.my}
                                onChange={e => dispatch(setFilter({ my: (e.target as HTMLInputElement).checked }))} />
                            <span className={`btn btn-xs w-full ${filters.my ? 'btn-accent' : 'btn-ghost border border-base-300'}`}>Мои</span>
                        </label>
                        <label className="cursor-pointer flex-1">
                            <input type="checkbox" className="hidden" checked={filters.protection}
                                onChange={e => dispatch(setFilter({ protection: (e.target as HTMLInputElement).checked }))} />
                            <span className={`btn btn-xs w-full ${filters.protection ? 'btn-accent' : 'btn-ghost border border-base-300'}`}>Защита</span>
                        </label>
                    </div>

                    {hasActiveFilters && (
                        <button className="btn btn-xs btn-ghost text-base-content/50" onClick={() => dispatch(resetFilters())}>
                            Сбросить фильтры
                        </button>
                    )}
                </div>
            )}
        </div>
    );
});

export default FilterBar;
